import { useState, useEffect, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, CustomFieldDefinition, EvidenceTag, InternalControl } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { useDatePreferences } from '../hooks/useDatePreferences'
import { useToast } from '../components/Toast'
import { useConfirm } from '../components/ConfirmModal'
import Breadcrumb from '../components/Breadcrumb'
import { EvidencePreview } from '../components/EvidencePreview'
import CustomFieldsForm from '../components/CustomFieldsForm'
import {
  FileText,
  Upload,
  Download,
  Trash2,
  Eye,
  X,
  File,
  Image,
  FileArchive,
  Link2,
  History,
  Plus,
  Clock,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Archive,
  ArchiveRestore,
  Tag,
  Tags,
  AlertTriangle,
  CalendarClock,
  Camera,
  Cloud,
} from 'lucide-react'
import ScreenCapture from '../components/ScreenCapture'

interface Evidence {
  id: string
  control_id: string
  control_number?: string
  control_name?: string
  note?: string
  timestamp: number
  created_at?: number
  updated_at?: number
  file_key?: string
  file_name?: string
  file_size?: number
  file_type?: string
  source: string
  uploaded_by: string
  uploaded_by_username?: string
  current_version?: number
  version_count?: number
  linked_controls?: Array<{
    control_id: string
    control_number: string
    name: string
    is_primary: number
  }>
  // Archive fields (#182)
  archived_at?: number
  archive_reason?: string
  archived_by?: string
  // Tags (#151)
  evidence_tags?: EvidenceTag[]
  // Expiration fields (#36)
  expires_at?: number
  renewal_status?: 'current' | 'expiring_soon' | 'expired'
  expiration_notes?: string
  // Integration metadata (Issue #318 - Azure Integration)
  metadata?: Record<string, any>
}

interface Control {
  id: string
  control_number: string
  name: string
  framework_name?: string
}

interface EvidenceVersion {
  id: string
  version: number
  file_name: string
  file_size: number
  file_type?: string
  note?: string
  change_reason?: string
  created_at: number
  created_by_username?: string
}

interface AuditLog {
  id: string
  action: string
  user_id: string
  username?: string
  user_username?: string
  details?: Record<string, any>
  ip_address?: string
  created_at: number
}

// Helper to parse evidence metadata from JSON string (Issue #318 - Azure Integration)
function parseEvidenceMetadata(evidence: Evidence): Evidence {
  return {
    ...evidence,
    metadata: evidence.metadata && typeof evidence.metadata === 'string'
      ? JSON.parse(evidence.metadata)
      : evidence.metadata,
  }
}


export default function Evidence() {
  const { user } = useAuth()
  const { formatDate: formatDatePref, formatDateTime, formatRelativeTime } = useDatePreferences()
  const toast = useToast()
  const confirm = useConfirm()
  const [searchParams] = useSearchParams()
  const [evidence, setEvidence] = useState<Evidence[]>([])
  const [controls, setControls] = useState<Control[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [controlFilter, setControlFilter] = useState(searchParams.get('control_id') || '')
  const [sourceFilter, setSourceFilter] = useState(searchParams.get('source') || '')
  const [showUploadModal, setShowUploadModal] = useState(
    searchParams.get('action') === 'upload'
  )
  const [showScreenCapture, setShowScreenCapture] = useState(false)
  const [selectedEvidence, setSelectedEvidence] = useState<Evidence | null>(null)
  const [previewEvidence, setPreviewEvidence] = useState<Evidence | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [deleting, setDeleting] = useState<string | null>(null)

  // New state for multi-control selection (#116)
  const [selectedControlIds, setSelectedControlIds] = useState<string[]>([])
  const [showControlPicker, setShowControlPicker] = useState(false)

  // New state for bulk upload (#53)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])

  // New state for version history (#47)
  const [showVersionHistory, setShowVersionHistory] = useState(false)
  const [versionHistory, setVersionHistory] = useState<EvidenceVersion[]>([])
  const [loadingVersions, setLoadingVersions] = useState(false)
  const [showNewVersionModal, setShowNewVersionModal] = useState(false)
  const [uploadingVersion, setUploadingVersion] = useState(false)

  // New state for audit trail (#137)
  const [showAuditTrail, setShowAuditTrail] = useState(false)
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([])
  const [loadingAudit, setLoadingAudit] = useState(false)

  // Link controls modal
  const [showLinkControlsModal, setShowLinkControlsModal] = useState(false)
  const [linkingControls, setLinkingControls] = useState(false)
  const [controlsToLink, setControlsToLink] = useState<string[]>([])

  // Archive filter and state (#182)
  const [archiveFilter, setArchiveFilter] = useState<'active' | 'archived' | 'all'>('active')
  const [archiving, setArchiving] = useState<string | null>(null)
  const [restoring, setRestoring] = useState<string | null>(null)

  // Custom fields state (#162)
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState<CustomFieldDefinition[]>([])
  const [loadingCustomFields, setLoadingCustomFields] = useState(false)
  const [uploadCustomFields, setUploadCustomFields] = useState<Record<string, any>>({})

  // Tags state (#151)
  const [allTags, setAllTags] = useState<EvidenceTag[]>([])
  const [tagFilter, setTagFilter] = useState<string[]>([])
  const [showTagFilter, setShowTagFilter] = useState(false)
  const [showTagModal, setShowTagModal] = useState(false)
  const [selectedEvidenceTags, setSelectedEvidenceTags] = useState<EvidenceTag[]>([])
  const [loadingTags, setLoadingTags] = useState(false)
  const [newTagName, setNewTagName] = useState('')
  const [newTagColor, setNewTagColor] = useState('#6B7280')
  const [creatingTag, setCreatingTag] = useState(false)
  const [tagsToAdd, setTagsToAdd] = useState<string[]>([])
  const [addingTags, setAddingTags] = useState(false)

  // Internal controls state (#311)
  const [internalControls, setInternalControls] = useState<InternalControl[]>([])
  const [selectedInternalControlId, setSelectedInternalControlId] = useState<string>('')
  const [showInternalControlPicker, setShowInternalControlPicker] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const newVersionInputRef = useRef<HTMLInputElement>(null)
  const limit = 20

  useEffect(() => {
    async function fetchControls() {
      try {
        // Only fetch controls from ENABLED frameworks - evidence should not be linked to inactive frameworks
        const res = await api.getControls({ limit: 500 })
        if (res.success) {
          setControls(res.controls)
        }
      } catch (err) {
        console.error('Failed to load controls:', err)
      }
    }
    fetchControls()
  }, [])

  // Fetch custom field definitions for evidence (#162)
  useEffect(() => {
    async function fetchCustomFieldDefinitions() {
      setLoadingCustomFields(true)
      try {
        const res = await api.getCustomFieldDefinitions('evidence')
        if (res.success) {
          setCustomFieldDefinitions(res.definitions)
        }
      } catch (err) {
        console.error('Failed to load custom field definitions:', err)
      } finally {
        setLoadingCustomFields(false)
      }
    }
    fetchCustomFieldDefinitions()
  }, [])

  // Fetch all tags for filtering (#151)
  useEffect(() => {
    async function fetchTags() {
      try {
        const res = await api.getEvidenceTags({ include_unused: true, limit: 200 })
        if (res.success) {
          setAllTags(res.tags)
        }
      } catch (err) {
        console.error('Failed to load tags:', err)
      }
    }
    fetchTags()
  }, [])

  // Fetch internal controls (#311)
  useEffect(() => {
    async function fetchInternalControls() {
      try {
        const res = await api.getInternalControls({ limit: 200 })
        if (res.success) {
          setInternalControls(res.data)
        }
      } catch (err) {
        console.error('Failed to load internal controls:', err)
      }
    }
    fetchInternalControls()
  }, [])

  useEffect(() => {
    fetchEvidence()
  }, [page, controlFilter, sourceFilter, archiveFilter, tagFilter])

  async function fetchEvidence() {
    setLoading(true)
    try {
      const res = await api.getEvidence({
        limit,
        offset: page * limit,
        control_id: controlFilter || undefined,
        source: sourceFilter || undefined,
        // Archive filter support (#182)
        include_archived: archiveFilter === 'all' ? true : undefined,
        archived_only: archiveFilter === 'archived' ? true : undefined,
        // Tag filter support (#151)
        tags: tagFilter.length > 0 ? tagFilter.join(',') : undefined,
      })
      if (res.success) {
        // Parse metadata from JSON string (Issue #318 - Azure Integration)
        setEvidence(res.evidence.map(parseEvidenceMetadata))
        setTotal(res.pagination.total)
      }
    } catch (err) {
      console.error('Failed to load evidence:', err)
    } finally {
      setLoading(false)
    }
  }

  // Fetch tags for a specific evidence item (#151)
  const fetchEvidenceTags = useCallback(async (evidenceId: string) => {
    setLoadingTags(true)
    try {
      const res = await api.getEvidenceItemTags(evidenceId)
      if (res.success) {
        setSelectedEvidenceTags(res.tags)
      }
    } catch (err) {
      console.error('Failed to load evidence tags:', err)
    } finally {
      setLoadingTags(false)
    }
  }, [])

  // Add tags to evidence (#151)
  const handleAddTags = async () => {
    if (!selectedEvidence || tagsToAdd.length === 0) return

    setAddingTags(true)
    try {
      const res = await api.addEvidenceTags(selectedEvidence.id, tagsToAdd)
      if (res.success) {
        toast.success(`Added ${res.applied_count} tag(s)`)
        setTagsToAdd([])
        fetchEvidenceTags(selectedEvidence.id)
        // Refresh all tags to update counts
        const tagsRes = await api.getEvidenceTags({ include_unused: true, limit: 200 })
        if (tagsRes.success) {
          setAllTags(tagsRes.tags)
        }
        fetchEvidence()
      }
    } catch (err) {
      toast.error('Failed to add tags')
    } finally {
      setAddingTags(false)
    }
  }

  // Remove tag from evidence (#151)
  const handleRemoveTag = async (tagId: string) => {
    if (!selectedEvidence) return

    try {
      await api.removeEvidenceTag(selectedEvidence.id, tagId)
      toast.success('Tag removed')
      fetchEvidenceTags(selectedEvidence.id)
      // Refresh all tags to update counts
      const tagsRes = await api.getEvidenceTags({ include_unused: true, limit: 200 })
      if (tagsRes.success) {
        setAllTags(tagsRes.tags)
      }
      fetchEvidence()
    } catch (err) {
      toast.error('Failed to remove tag')
    }
  }

  // Create a new tag (#151)
  const handleCreateTag = async () => {
    if (!newTagName.trim()) return

    setCreatingTag(true)
    try {
      const res = await api.createEvidenceTag({
        name: newTagName.trim(),
        color: newTagColor,
        category: 'general',
      })
      if (res.success) {
        toast.success('Tag created')
        setNewTagName('')
        setNewTagColor('#6B7280')
        setAllTags(prev => [...prev, res.tag])
      }
    } catch (err) {
      toast.error('Failed to create tag')
    } finally {
      setCreatingTag(false)
    }
  }

  // Fetch version history (#47)
  const fetchVersionHistory = useCallback(async (evidenceId: string) => {
    setLoadingVersions(true)
    try {
      const res = await api.getEvidenceVersions(evidenceId)
      if (res.success) {
        setVersionHistory(res.versions)
      }
    } catch (err) {
      console.error('Failed to load version history:', err)
    } finally {
      setLoadingVersions(false)
    }
  }, [])

  // Fetch audit trail (#137)
  const fetchAuditTrail = useCallback(async (evidenceId: string) => {
    setLoadingAudit(true)
    try {
      const res = await api.getEvidenceAudit(evidenceId)
      if (res.success) {
        setAuditLogs(res.audit_logs)
      }
    } catch (err) {
      console.error('Failed to load audit trail:', err)
    } finally {
      setLoadingAudit(false)
    }
  }, [])

  // Handle screenshot capture (#95)
  const handleScreenCapture = (blob: Blob, fileName: string) => {
    // Convert blob to File object (using globalThis.File because File from lucide-react shadows the global)
    const file = new globalThis.File([blob], fileName, { type: 'image/png' })
    setSelectedFiles(prev => [...prev, file])
    setShowScreenCapture(false)
    setShowUploadModal(true) // Open upload modal to complete the upload
  }

  // Handle upload with multi-file and multi-control support (#53, #116, #311)
  const handleUpload = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setUploadError('')
    setUploading(true)

    const form = e.currentTarget
    const formData = new FormData()

    try {
      // Validate at least one control (internal or framework) is selected
      if (selectedControlIds.length === 0 && !selectedInternalControlId) {
        throw new Error('Please select an internal control or at least one framework control.')
      }

      // Add internal_control_id if selected (#311)
      if (selectedInternalControlId) {
        formData.append('internal_control_id', selectedInternalControlId)
      }

      // Add control_ids to form data (#116) - only if selected
      if (selectedControlIds.length > 0) {
        formData.append('control_ids', JSON.stringify(selectedControlIds))
        formData.append('control_id', selectedControlIds[0]) // Primary control
      }

      // Add note and tags
      const note = (form.elements.namedItem('note') as HTMLTextAreaElement)?.value
      const tags = (form.elements.namedItem('tags') as HTMLInputElement)?.value

      if (note) formData.append('note', note)
      if (tags) formData.append('tags', tags)

      // Add custom fields (#162)
      if (Object.keys(uploadCustomFields).length > 0) {
        formData.append('custom_fields', JSON.stringify(uploadCustomFields))
      }

      // Handle single or multiple files (#53)
      if (selectedFiles.length === 0 && !note) {
        throw new Error('Please upload a file or add a note.')
      }

      for (const file of selectedFiles) {
        if (file.size > 50 * 1024 * 1024) {
          throw new Error(`File "${file.name}" is too large. Maximum size is 50MB.`)
        }
      }

      // Upload files
      if (selectedFiles.length === 1) {
        formData.append('file', selectedFiles[0])
      } else if (selectedFiles.length > 1) {
        selectedFiles.forEach(file => formData.append('files', file))
      }

      const res = await api.uploadEvidence(formData)
      if (res.success) {
        setShowUploadModal(false)
        form.reset()
        setSelectedFiles([])
        setSelectedControlIds([])
        setSelectedInternalControlId('')
        setUploadCustomFields({})
        toast.success(`Evidence uploaded successfully${(res.count ?? 1) > 1 ? ` (${res.count} files)` : ''}`)
        fetchEvidence()
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  // Handle new version upload (#47)
  const handleNewVersion = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!selectedEvidence) return

    setUploadingVersion(true)
    const form = e.currentTarget
    const formData = new FormData()

    try {
      const file = newVersionInputRef.current?.files?.[0]
      if (!file) {
        throw new Error('Please select a file.')
      }

      if (file.size > 50 * 1024 * 1024) {
        throw new Error('File too large. Maximum size is 50MB.')
      }

      formData.append('file', file)
      const changeReason = (form.elements.namedItem('change_reason') as HTMLInputElement)?.value
      if (changeReason) {
        formData.append('change_reason', changeReason)
      }

      const res = await api.uploadEvidenceVersion(selectedEvidence.id, formData)

      if (res.success) {
        setShowNewVersionModal(false)
        form.reset()
        toast.success('New version uploaded successfully')
        // Refresh evidence details
        const updatedRes = await api.getEvidenceItem(selectedEvidence.id)
        if (updatedRes.success) {
          setSelectedEvidence(parseEvidenceMetadata(updatedRes.evidence))
        }
        fetchVersionHistory(selectedEvidence.id)
        fetchEvidence()
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload new version')
    } finally {
      setUploadingVersion(false)
    }
  }

  // Handle linking controls (#116)
  const handleLinkControls = async () => {
    if (!selectedEvidence || controlsToLink.length === 0) return

    setLinkingControls(true)
    try {
      const res = await api.linkEvidenceControls(selectedEvidence.id, controlsToLink)

      if (res.success) {
        setShowLinkControlsModal(false)
        setControlsToLink([])
        toast.success(`Linked ${res.linked_count} control(s)`)
        // Refresh evidence details
        const updatedRes = await api.getEvidenceItem(selectedEvidence.id)
        if (updatedRes.success) {
          setSelectedEvidence(parseEvidenceMetadata(updatedRes.evidence))
        }
        fetchEvidence()
      }
    } catch (err) {
      toast.error('Failed to link controls')
    } finally {
      setLinkingControls(false)
    }
  }

  // Handle unlinking a control (#116)
  const handleUnlinkControl = async (controlId: string) => {
    if (!selectedEvidence) return

    const confirmed = await confirm({
      title: 'Unlink Control',
      message: 'Are you sure you want to unlink this control from the evidence?',
      type: 'warning',
      confirmText: 'Unlink',
      cancelText: 'Cancel',
    })

    if (!confirmed) return

    try {
      await api.unlinkEvidenceControl(selectedEvidence.id, controlId)
      toast.success('Control unlinked')
      // Refresh evidence details
      const updatedRes = await api.getEvidenceItem(selectedEvidence.id)
      if (updatedRes.success) {
        setSelectedEvidence(parseEvidenceMetadata(updatedRes.evidence))
      }
    } catch (err) {
      toast.error('Failed to unlink control')
    }
  }

  // Handle authenticated file download
  const handleDownload = async (evidenceId: string, fileName?: string) => {
    try {
      const url = api.getEvidenceDownloadUrl(evidenceId)
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('grc_token')}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`)
      }

      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = fileName || 'download'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Download failed')
    }
  }

  // Handle authenticated version download
  const handleVersionDownload = async (evidenceId: string, version: number, fileName?: string) => {
    try {
      const url = `${api.getEvidenceDownloadUrl(evidenceId).replace('/download', '')}/versions/${version}/download`
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('grc_token')}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`)
      }

      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = fileName || 'download'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Download failed')
    }
  }

  const handleDelete = async (item: Evidence) => {
    const confirmed = await confirm({
      title: 'Delete Evidence',
      message: `Are you sure you want to delete "${item.file_name || item.note || 'this evidence'}"? This action cannot be undone.`,
      type: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel',
    })

    if (!confirmed) return

    setDeleting(item.id)
    try {
      const res = await api.deleteEvidence(item.id)
      if (res.success) {
        toast.success('Evidence deleted successfully')
        setSelectedEvidence(null)
        fetchEvidence()
      } else {
        toast.error('Failed to delete evidence')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete evidence')
    } finally {
      setDeleting(null)
    }
  }

  // Archive evidence (#182)
  const handleArchive = async (item: Evidence) => {
    const confirmed = await confirm({
      title: 'Archive Evidence',
      message: `Are you sure you want to archive "${item.file_name || item.note || 'this evidence'}"? You can restore it later from the archived view.`,
      type: 'warning',
      confirmText: 'Archive',
      cancelText: 'Cancel',
    })

    if (!confirmed) return

    setArchiving(item.id)
    try {
      const res = await api.archiveEvidence(item.id, 'Manually archived by user')
      if (res.success) {
        toast.success('Evidence archived successfully')
        setSelectedEvidence(null)
        fetchEvidence()
      } else {
        toast.error('Failed to archive evidence')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to archive evidence')
    } finally {
      setArchiving(null)
    }
  }

  // Restore archived evidence (#182)
  const handleRestore = async (item: Evidence) => {
    const confirmed = await confirm({
      title: 'Restore Evidence',
      message: `Are you sure you want to restore "${item.file_name || item.note || 'this evidence'}"?`,
      type: 'info',
      confirmText: 'Restore',
      cancelText: 'Cancel',
    })

    if (!confirmed) return

    setRestoring(item.id)
    try {
      const res = await api.restoreEvidence(item.id)
      if (res.success) {
        toast.success('Evidence restored successfully')
        setSelectedEvidence(null)
        fetchEvidence()
      } else {
        toast.error('Failed to restore evidence')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to restore evidence')
    } finally {
      setRestoring(null)
    }
  }

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '-'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const getFileIcon = (type?: string) => {
    if (!type) return File
    if (type.startsWith('image/')) return Image
    if (type.includes('zip') || type.includes('archive')) return FileArchive
    return FileText
  }

  const isPreviewable = (type?: string) => {
    if (!type) return false
    return (
      type.startsWith('image/') ||
      type === 'application/pdf' ||
      type.startsWith('text/') ||
      type === 'application/json' ||
      type === 'application/xml' ||
      // Word documents
      type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      type === 'application/msword' ||
      // Excel files
      type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      type === 'application/vnd.ms-excel'
    )
  }

  // Check if evidence can be previewed (including Azure integration evidence)
  const canPreview = (item: Evidence) => {
    // Azure integration evidence (has metadata, no file)
    if (item.source === 'azure_integration' && item.metadata) {
      return true
    }
    // Regular file evidence
    return item.file_key && isPreviewable(item.file_type)
  }

  const formatDate = (timestamp: number) => {
    return formatDateTime(timestamp, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      created: 'Created',
      uploaded: 'Uploaded',
      downloaded: 'Downloaded',
      viewed: 'Viewed',
      updated: 'Updated',
      deleted: 'Deleted',
      version_created: 'New Version',
      control_linked: 'Control Linked',
      control_unlinked: 'Control Unlinked',
    }
    return labels[action] || action
  }

  // Toggle control selection for multi-select (#116)
  const toggleControlSelection = (controlId: string) => {
    setSelectedControlIds(prev =>
      prev.includes(controlId)
        ? prev.filter(id => id !== controlId)
        : [...prev, controlId]
    )
  }

  // Group controls by framework for the picker
  const groupedControls = controls.reduce((acc, c) => {
    const fw = c.framework_name || 'Other'
    if (!acc[fw]) acc[fw] = []
    acc[fw].push(c)
    return acc
  }, {} as Record<string, Control[]>)

  return (
    <div className="space-y-6">
      <Breadcrumb />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Evidence</h1>
          <p className="text-gray-500">Compliance evidence and documentation</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowScreenCapture(true)} className="btn-secondary">
            <Camera className="h-4 w-4 mr-2" />
            Capture Screen
          </button>
          <button onClick={() => setShowUploadModal(true)} className="btn-primary">
            <Upload className="h-4 w-4 mr-2" />
            Upload Evidence
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <select
              value={controlFilter}
              onChange={(e) => {
                setControlFilter(e.target.value)
                setPage(0)
              }}
              className="input"
            >
              <option value="">All Controls</option>
              {Object.entries(groupedControls).map(([frameworkName, frameworkControls]) => (
                <optgroup key={frameworkName} label={frameworkName}>
                  {frameworkControls.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.control_number} - {c.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div className="sm:w-48">
            <select
              value={sourceFilter}
              onChange={(e) => {
                setSourceFilter(e.target.value)
                setPage(0)
              }}
              className="input"
            >
              <option value="">All Sources</option>
              <option value="manual">Manual Upload</option>
              <option value="aws">AWS</option>
              <option value="azure">Azure</option>
              <option value="gcp">GCP</option>
              <option value="jira">Jira</option>
            </select>
          </div>
          {/* Archive filter (#182) */}
          <div className="sm:w-40">
            <select
              value={archiveFilter}
              onChange={(e) => {
                setArchiveFilter(e.target.value as 'active' | 'archived' | 'all')
                setPage(0)
              }}
              className="input"
            >
              <option value="active">Active Only</option>
              <option value="archived">Archived Only</option>
              <option value="all">All Evidence</option>
            </select>
          </div>
          {/* Tag filter (#151) */}
          <div className="sm:w-48 relative">
            <button
              type="button"
              onClick={() => setShowTagFilter(!showTagFilter)}
              className="input w-full text-left flex items-center justify-between"
            >
              <span className={tagFilter.length === 0 ? 'text-gray-400' : ''}>
                {tagFilter.length === 0
                  ? 'Filter by tags'
                  : `${tagFilter.length} tag(s) selected`}
              </span>
              <Tags className="h-4 w-4 text-gray-400" />
            </button>
            {showTagFilter && (
              <div className="absolute z-10 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto right-0">
                <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
                  <span className="text-xs font-medium text-gray-500 uppercase">Filter by Tags</span>
                </div>
                {allTags.length === 0 ? (
                  <div className="p-3 text-sm text-gray-500 text-center">No tags available</div>
                ) : (
                  allTags.map((t) => (
                    <label
                      key={t.id}
                      className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={tagFilter.includes(t.slug)}
                        onChange={() => {
                          setTagFilter(prev =>
                            prev.includes(t.slug)
                              ? prev.filter(s => s !== t.slug)
                              : [...prev, t.slug]
                          )
                          setPage(0)
                        }}
                        className="h-4 w-4 text-primary-600 rounded border-gray-300"
                      />
                      <span
                        className="ml-2 inline-flex items-center gap-1.5"
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: t.color }}
                        />
                        <span className="text-sm">{t.name}</span>
                        <span className="text-xs text-gray-400">({t.use_count})</span>
                      </span>
                    </label>
                  ))
                )}
                {tagFilter.length > 0 && (
                  <div className="p-2 border-t border-gray-100 sticky bottom-0 bg-white">
                    <button
                      onClick={() => {
                        setTagFilter([])
                        setShowTagFilter(false)
                      }}
                      className="text-xs text-primary-600 hover:text-primary-700"
                    >
                      Clear all filters
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Active tag filters display (#151) */}
        {tagFilter.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            <span className="text-xs text-gray-500 py-1">Filtering by:</span>
            {tagFilter.map(slug => {
              const t = allTags.find(tag => tag.slug === slug)
              return t ? (
                <span
                  key={slug}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs"
                  style={{ backgroundColor: `${t.color}20`, color: t.color }}
                >
                  {t.name}
                  <button
                    onClick={() => setTagFilter(prev => prev.filter(s => s !== slug))}
                    className="hover:opacity-70"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ) : null
            })}
          </div>
        )}
      </div>

      {/* Evidence List */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : evidence.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No evidence found</p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="btn-primary mt-4"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload your first evidence
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Evidence
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Control(s)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Uploaded
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Modified
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {evidence.map((item) => {
                    const FileIcon = getFileIcon(item.file_type)
                    const linkedCount = (item.linked_controls?.length || 0) + (item.control_id ? 1 : 0)
                    return (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-start">
                            <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center">
                              <FileIcon className="h-5 w-5 text-gray-500" />
                            </div>
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900 line-clamp-1">
                                {item.file_name || item.note || 'Evidence note'}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <span>{item.file_size ? formatFileSize(item.file_size) : 'Text note'}</span>
                                {(item.current_version || 0) > 1 && (
                                  <span className="px-1.5 py-0.5 bg-primary-100 text-primary-700 rounded text-xs">v{item.current_version}</span>
                                )}
                                {/* Archived badge (#182) */}
                                {item.archived_at && (
                                  <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs flex items-center gap-1">
                                    <Archive className="h-3 w-3" />
                                    Archived
                                  </span>
                                )}
                                {/* Expiration indicator (#36) */}
                                {item.expires_at && (
                                  item.renewal_status === 'expired' ? (
                                    <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs flex items-center gap-1" title={`Expired: ${formatDate(item.expires_at)}`}>
                                      <AlertTriangle className="h-3 w-3" />
                                      Expired
                                    </span>
                                  ) : item.renewal_status === 'expiring_soon' ? (
                                    <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-xs flex items-center gap-1" title={`Expires: ${formatDate(item.expires_at)}`}>
                                      <CalendarClock className="h-3 w-3" />
                                      Expiring
                                    </span>
                                  ) : (
                                    <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs flex items-center gap-1" title={`Expires: ${formatDate(item.expires_at)}`}>
                                      <CalendarClock className="h-3 w-3" />
                                      {formatDate(item.expires_at)}
                                    </span>
                                  )
                                )}
                              </div>
                              {/* Tags (#151) */}
                              {item.evidence_tags && item.evidence_tags.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {item.evidence_tags.slice(0, 3).map((t) => (
                                    <span
                                      key={t.id}
                                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs"
                                      style={{ backgroundColor: `${t.color}20`, color: t.color }}
                                      title={t.source === 'auto' ? 'Auto-tagged' : 'Manual tag'}
                                    >
                                      {t.name}
                                    </span>
                                  ))}
                                  {item.evidence_tags.length > 3 && (
                                    <span className="text-xs text-gray-400">+{item.evidence_tags.length - 3}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <span className="text-sm text-gray-700">
                              {item.control_number || '-'}
                            </span>
                            {linkedCount > 1 && (
                              <span className="badge-gray text-xs">+{linkedCount - 1}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="badge-gray capitalize">{item.source}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-700">
                            {formatDate(item.timestamp)}
                          </div>
                          <div className="text-xs text-gray-500">
                            by {item.uploaded_by_username || 'Unknown'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {item.updated_at && item.updated_at !== item.created_at ? (
                            <div className="text-sm text-gray-700">
                              {formatDate(item.updated_at)}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2">
                            {/* Preview button for files and Azure integration evidence */}
                            {canPreview(item) && (
                              <button
                                onClick={() => setPreviewEvidence(item)}
                                className="p-2 text-primary-500 hover:text-primary-700 hover:bg-primary-50 rounded"
                                title={item.source === 'azure_integration' ? 'View Azure data' : 'Preview file'}
                              >
                                {item.source === 'azure_integration' ? (
                                  <Cloud className="h-4 w-4" />
                                ) : item.file_type?.startsWith('image/') ? (
                                  <Image className="h-4 w-4" />
                                ) : (
                                  <FileText className="h-4 w-4" />
                                )}
                              </button>
                            )}
                            <button
                              onClick={async () => {
                                // Fetch full evidence details including linked_controls
                                try {
                                  const res = await api.getEvidenceItem(item.id)
                                  if (res.success) {
                                    setSelectedEvidence(parseEvidenceMetadata(res.evidence))
                                  }
                                } catch {
                                  setSelectedEvidence(parseEvidenceMetadata(item))
                                }
                                setShowVersionHistory(false)
                                setShowAuditTrail(false)
                                setShowTagModal(false)
                                // Fetch tags for this evidence (#151)
                                fetchEvidenceTags(item.id)
                              }}
                              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                              title="View details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {item.file_key && (
                              <button
                                onClick={() => handleDownload(item.id, item.file_name)}
                                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                                title="Download"
                              >
                                <Download className="h-4 w-4" />
                              </button>
                            )}
                            {/* Archive/Restore button (#182) */}
                            {(user?.role === 'admin' || user?.id === item.uploaded_by) && (
                              item.archived_at ? (
                                <button
                                  onClick={() => handleRestore(item)}
                                  disabled={restoring === item.id}
                                  className="p-2 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 rounded disabled:opacity-50"
                                  title="Restore from archive"
                                >
                                  {restoring === item.id ? (
                                    <span className="w-4 h-4 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin inline-block" />
                                  ) : (
                                    <ArchiveRestore className="h-4 w-4" />
                                  )}
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleArchive(item)}
                                  disabled={archiving === item.id}
                                  className="p-2 text-amber-500 hover:text-amber-700 hover:bg-amber-50 rounded disabled:opacity-50"
                                  title="Archive"
                                >
                                  {archiving === item.id ? (
                                    <span className="w-4 h-4 border-2 border-amber-300 border-t-amber-600 rounded-full animate-spin inline-block" />
                                  ) : (
                                    <Archive className="h-4 w-4" />
                                  )}
                                </button>
                              )
                            )}
                            {(user?.role === 'admin' ||
                              user?.id === item.uploaded_by) && (
                              <button
                                onClick={() => handleDelete(item)}
                                disabled={deleting === item.id}
                                className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded disabled:opacity-50"
                                title="Delete"
                              >
                                {deleting === item.id ? (
                                  <span className="w-4 h-4 border-2 border-red-300 border-t-red-600 rounded-full animate-spin inline-block" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of{' '}
                {total} items
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="btn-secondary disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={(page + 1) * limit >= total}
                  className="btn-secondary disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Upload Modal - Enhanced with multi-file and multi-control (#53, #116) */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Upload Evidence</h2>
              <button
                onClick={() => {
                  setShowUploadModal(false)
                  setSelectedFiles([])
                  setSelectedControlIds([])
                  setSelectedInternalControlId('')
                  setUploadError('')
                }}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleUpload} className="p-4 space-y-4">
              {uploadError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {uploadError}
                </div>
              )}

              {/* Internal Control Picker (#311) */}
              <div>
                <label className="label">Internal Control (recommended)</label>
                <p className="text-xs text-gray-500 mb-2">
                  Select an internal control to link evidence directly. This automatically satisfies all mapped framework requirements.
                </p>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setShowInternalControlPicker(!showInternalControlPicker)
                      setShowControlPicker(false)
                    }}
                    className="input w-full text-left flex items-center justify-between"
                  >
                    <span className={!selectedInternalControlId ? 'text-gray-400' : ''}>
                      {selectedInternalControlId
                        ? internalControls.find(ic => ic.id === selectedInternalControlId)?.name || 'Selected'
                        : 'Select internal control...'}
                    </span>
                    {showInternalControlPicker ? (
                      <ChevronUp className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    )}
                  </button>

                  {showInternalControlPicker && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedInternalControlId('')
                          setShowInternalControlPicker(false)
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-50"
                      >
                        None (use framework controls instead)
                      </button>
                      {['Identify', 'Protect', 'Detect', 'Respond', 'Recover'].map(category => {
                        const categoryControls = internalControls.filter(ic => ic.category === category)
                        if (categoryControls.length === 0) return null
                        return (
                          <div key={category}>
                            <div className="px-3 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase sticky top-0">
                              {category}
                            </div>
                            {categoryControls.map((ic) => (
                              <button
                                key={ic.id}
                                type="button"
                                onClick={() => {
                                  setSelectedInternalControlId(ic.id)
                                  setShowInternalControlPicker(false)
                                }}
                                className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between ${
                                  selectedInternalControlId === ic.id ? 'bg-primary-50' : ''
                                }`}
                              >
                                <span className="text-sm">
                                  <span className="font-mono text-gray-500 mr-2">{ic.control_number}</span>
                                  {ic.name}
                                </span>
                                {(ic.framework_control_count || 0) > 0 && (
                                  <span className="text-xs text-gray-400">
                                    {ic.framework_control_count} frameworks
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
                {selectedInternalControlId && (
                  <div className="mt-2">
                    {(() => {
                      const ic = internalControls.find(c => c.id === selectedInternalControlId)
                      return ic ? (
                        <div className="flex items-center gap-2 bg-primary-50 border border-primary-200 rounded-lg px-3 py-2">
                          <div className="flex-1">
                            <span className="text-sm font-medium text-primary-900">{ic.control_number}</span>
                            <span className="text-sm text-primary-700 ml-2">{ic.name}</span>
                            {(ic.framework_control_count || 0) > 0 && (
                              <p className="text-xs text-primary-600 mt-0.5">
                                Satisfies {ic.framework_control_count} framework requirement(s)
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => setSelectedInternalControlId('')}
                            className="text-primary-500 hover:text-primary-700"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : null
                    })()}
                  </div>
                )}
              </div>

              {/* Multi-Control Picker (#116) - Optional when internal control is selected */}
              <div>
                <label className="label">
                  Framework Controls {selectedInternalControlId ? '(optional)' : '* (select one or more)'}
                </label>
                {selectedInternalControlId && (
                  <p className="text-xs text-gray-500 mb-2">
                    Optional: You can also link directly to specific framework controls if needed.
                  </p>
                )}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setShowControlPicker(!showControlPicker)
                      setShowInternalControlPicker(false)
                    }}
                    className="input w-full text-left flex items-center justify-between"
                  >
                    <span className={selectedControlIds.length === 0 ? 'text-gray-400' : ''}>
                      {selectedControlIds.length === 0
                        ? 'Select framework controls...'
                        : `${selectedControlIds.length} control(s) selected`}
                    </span>
                    {showControlPicker ? (
                      <ChevronUp className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    )}
                  </button>

                  {showControlPicker && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {Object.entries(groupedControls).map(([frameworkName, frameworkControls]) => (
                        <div key={frameworkName}>
                          <div className="px-3 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase sticky top-0">
                            {frameworkName}
                          </div>
                          {frameworkControls.map((c) => (
                            <label
                              key={c.id}
                              className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedControlIds.includes(c.id)}
                                onChange={() => toggleControlSelection(c.id)}
                                className="h-4 w-4 text-primary-600 rounded border-gray-300"
                              />
                              <span className="ml-2 text-sm">
                                {c.control_number} - {c.name}
                              </span>
                            </label>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {selectedControlIds.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {selectedControlIds.map(id => {
                      const control = controls.find(c => c.id === id)
                      return control ? (
                        <span key={id} className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-700 rounded text-xs">
                          {control.control_number}
                          <button
                            type="button"
                            onClick={() => toggleControlSelection(id)}
                            className="hover:text-primary-900"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ) : null
                    })}
                  </div>
                )}
              </div>

              {/* Multi-File Upload (#53) */}
              <div>
                <label className="label">Files</label>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary-500 transition-colors"
                >
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Max file size: 50MB
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || [])
                    setSelectedFiles(prev => [...prev, ...files])
                  }}
                />
                {selectedFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                        <div className="flex items-center gap-2">
                          <File className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">{file.name}</span>
                          <span className="text-xs text-gray-500">({formatFileSize(file.size)})</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="label">Note</label>
                <textarea
                  name="note"
                  rows={3}
                  className="input"
                  placeholder="Add a description or note about this evidence..."
                />
              </div>

              <div>
                <label className="label">Tags (comma-separated)</label>
                <input
                  type="text"
                  name="tags"
                  className="input"
                  placeholder="e.g., quarterly-review, network, firewall"
                />
              </div>

              {/* Custom Fields (#162) */}
              <CustomFieldsForm
                fields={customFieldDefinitions}
                values={uploadCustomFields}
                onChange={(fieldName, value) =>
                  setUploadCustomFields({
                    ...uploadCustomFields,
                    [fieldName]: value,
                  })
                }
                loading={loadingCustomFields}
              />

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false)
                    setSelectedFiles([])
                    setSelectedControlIds([])
                    setSelectedInternalControlId('')
                    setUploadError('')
                    setUploadCustomFields({})
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" disabled={uploading} className="btn-primary">
                  {uploading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload {selectedFiles.length > 1 ? `${selectedFiles.length} Files` : 'Evidence'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Evidence Detail Modal - Enhanced with version history and audit trail */}
      {selectedEvidence && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Evidence Details</h2>
              <button
                onClick={() => {
                  setSelectedEvidence(null)
                  setShowVersionHistory(false)
                  setShowAuditTrail(false)
                }}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {selectedEvidence.file_name && (
                <div>
                  <label className="text-xs text-gray-500 uppercase">File</label>
                  <p className="text-sm font-medium">{selectedEvidence.file_name}</p>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{formatFileSize(selectedEvidence.file_size)}</span>
                    <span>{selectedEvidence.file_type}</span>
                    {(selectedEvidence.current_version || 0) > 1 && (
                      <span className="px-1.5 py-0.5 bg-primary-100 text-primary-700 rounded">Version {selectedEvidence.current_version}</span>
                    )}
                  </div>
                </div>
              )}

              {selectedEvidence.note && (
                <div>
                  <label className="text-xs text-gray-500 uppercase">Note</label>
                  <p className="text-sm">{selectedEvidence.note}</p>
                </div>
              )}

              {/* Linked Controls (#116) */}
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs text-gray-500 uppercase">Linked Controls</label>
                  <button
                    onClick={() => setShowLinkControlsModal(true)}
                    className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                  >
                    <Plus className="h-3 w-3" />
                    Link More
                  </button>
                </div>
                <div className="mt-1 space-y-1">
                  {/* Primary control */}
                  <div className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Link2 className="h-4 w-4 text-gray-400" />
                      <span className="text-sm font-medium">{selectedEvidence.control_number}</span>
                      <span className="px-1.5 py-0.5 bg-primary-100 text-primary-700 rounded text-xs">Primary</span>
                    </div>
                  </div>
                  {/* Additional linked controls */}
                  {selectedEvidence.linked_controls?.filter(lc => !lc.is_primary).map(lc => (
                    <div key={lc.control_id} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Link2 className="h-4 w-4 text-gray-400" />
                        <span className="text-sm">{lc.control_number}</span>
                      </div>
                      <button
                        onClick={() => handleUnlinkControl(lc.control_id)}
                        className="text-gray-400 hover:text-red-500"
                        title="Unlink"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tags (#151) */}
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs text-gray-500 uppercase">Tags</label>
                  <button
                    onClick={() => setShowTagModal(!showTagModal)}
                    className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                  >
                    <Tag className="h-3 w-3" />
                    {showTagModal ? 'Done' : 'Manage Tags'}
                  </button>
                </div>
                <div className="mt-1">
                  {loadingTags ? (
                    <div className="text-center py-2">
                      <RefreshCw className="h-4 w-4 animate-spin mx-auto text-gray-400" />
                    </div>
                  ) : selectedEvidenceTags.length === 0 ? (
                    <p className="text-sm text-gray-500">No tags</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {selectedEvidenceTags.map((t) => (
                        <span
                          key={t.id}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs"
                          style={{ backgroundColor: `${t.color}20`, color: t.color }}
                        >
                          {t.name}
                          {t.source === 'auto' && (
                            <span className="opacity-50" title="Auto-tagged">A</span>
                          )}
                          {(user?.role !== 'viewer') && (
                            <button
                              onClick={() => handleRemoveTag(t.id)}
                              className="hover:opacity-70 ml-0.5"
                              title="Remove tag"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Add tags panel (#151) */}
                  {showTagModal && user?.role !== 'viewer' && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-3">
                      {/* Select existing tags */}
                      <div>
                        <label className="text-xs font-medium text-gray-700">Add existing tags</label>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {allTags
                            .filter(t => !selectedEvidenceTags.some(st => st.id === t.id))
                            .map(t => (
                              <button
                                key={t.id}
                                onClick={() => {
                                  if (tagsToAdd.includes(t.id)) {
                                    setTagsToAdd(prev => prev.filter(id => id !== t.id))
                                  } else {
                                    setTagsToAdd(prev => [...prev, t.id])
                                  }
                                }}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs border transition-colors ${
                                  tagsToAdd.includes(t.id)
                                    ? 'border-primary-500 bg-primary-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                                style={{ color: tagsToAdd.includes(t.id) ? t.color : undefined }}
                              >
                                <span
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: t.color }}
                                />
                                {t.name}
                              </button>
                            ))}
                        </div>
                        {tagsToAdd.length > 0 && (
                          <button
                            onClick={handleAddTags}
                            disabled={addingTags}
                            className="mt-2 btn-primary text-xs py-1 px-2"
                          >
                            {addingTags ? (
                              <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <Plus className="h-3 w-3 mr-1" />
                            )}
                            Add {tagsToAdd.length} tag(s)
                          </button>
                        )}
                      </div>

                      {/* Create new tag */}
                      <div className="pt-3 border-t border-gray-200">
                        <label className="text-xs font-medium text-gray-700">Create new tag</label>
                        <div className="mt-1 flex gap-2">
                          <input
                            type="text"
                            value={newTagName}
                            onChange={(e) => setNewTagName(e.target.value)}
                            placeholder="Tag name"
                            className="input text-sm py-1 flex-1"
                          />
                          <input
                            type="color"
                            value={newTagColor}
                            onChange={(e) => setNewTagColor(e.target.value)}
                            className="w-10 h-8 rounded border border-gray-300 cursor-pointer"
                            title="Tag color"
                          />
                          <button
                            onClick={handleCreateTag}
                            disabled={creatingTag || !newTagName.trim()}
                            className="btn-primary text-xs py-1 px-2"
                          >
                            {creatingTag ? (
                              <RefreshCw className="h-3 w-3 animate-spin" />
                            ) : (
                              <Plus className="h-3 w-3" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase">Source</label>
                  <p className="text-sm capitalize">{selectedEvidence.source}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">Uploaded</label>
                  <p className="text-sm">{formatDate(selectedEvidence.timestamp)}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">Uploaded By</label>
                  <p className="text-sm">
                    {selectedEvidence.uploaded_by_username || 'Unknown'}
                  </p>
                </div>
                {selectedEvidence.updated_at && selectedEvidence.updated_at !== selectedEvidence.created_at && (
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Last Modified</label>
                    <p className="text-sm">{formatDate(selectedEvidence.updated_at)}</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                {/* Azure integration evidence preview button */}
                {canPreview(selectedEvidence) && !selectedEvidence.file_key && (
                  <button
                    onClick={() => {
                      setPreviewEvidence(selectedEvidence)
                      setSelectedEvidence(null)
                    }}
                    className="btn-primary flex-1"
                  >
                    <Cloud className="h-4 w-4 mr-2" />
                    View Azure Data
                  </button>
                )}
                {/* File-based evidence buttons */}
                {selectedEvidence.file_key && (
                  <>
                    {isPreviewable(selectedEvidence.file_type) && (
                      <button
                        onClick={() => {
                          setPreviewEvidence(selectedEvidence)
                          setSelectedEvidence(null)
                        }}
                        className="btn-secondary flex-1"
                      >
                        {selectedEvidence.file_type?.startsWith('image/') ? (
                          <Image className="h-4 w-4 mr-2" />
                        ) : (
                          <FileText className="h-4 w-4 mr-2" />
                        )}
                        Preview
                      </button>
                    )}
                    <button
                      onClick={() => handleDownload(selectedEvidence.id, selectedEvidence.file_name)}
                      className="btn-primary flex-1"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </button>
                    {(user?.role === 'admin' || user?.id === selectedEvidence.uploaded_by) && (
                      <button
                        onClick={() => setShowNewVersionModal(true)}
                        className="btn-secondary flex-1"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        New Version
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* Version History Toggle (#47) */}
              {selectedEvidence.file_key && (
                <div className="border-t pt-4">
                  <button
                    onClick={() => {
                      setShowVersionHistory(!showVersionHistory)
                      setShowAuditTrail(false)
                      if (!showVersionHistory) {
                        fetchVersionHistory(selectedEvidence.id)
                      }
                    }}
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
                  >
                    <History className="h-4 w-4" />
                    Version History
                    {showVersionHistory ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>

                  {showVersionHistory && (
                    <div className="mt-2 space-y-2">
                      {loadingVersions ? (
                        <div className="text-center py-4">
                          <RefreshCw className="h-5 w-5 animate-spin mx-auto text-gray-400" />
                        </div>
                      ) : versionHistory.length === 0 ? (
                        <p className="text-sm text-gray-500 py-2">No previous versions</p>
                      ) : (
                        versionHistory.map((version) => (
                          <div key={version.id} className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="text-sm font-medium">Version {version.version}</span>
                                <p className="text-xs text-gray-500">{version.file_name}</p>
                              </div>
                              <button
                                onClick={() => handleVersionDownload(selectedEvidence.id, version.version, version.file_name)}
                                className="p-1 text-gray-500 hover:text-gray-700"
                                title="Download this version"
                              >
                                <Download className="h-4 w-4" />
                              </button>
                            </div>
                            <div className="mt-1 text-xs text-gray-500">
                              {formatDate(version.created_at)} by {version.created_by_username || 'Unknown'}
                            </div>
                            {version.change_reason && (
                              <p className="mt-1 text-xs text-gray-600">{version.change_reason}</p>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Audit Trail Toggle (#137) */}
              <div className="border-t pt-4">
                <button
                  onClick={() => {
                    setShowAuditTrail(!showAuditTrail)
                    setShowVersionHistory(false)
                    if (!showAuditTrail) {
                      fetchAuditTrail(selectedEvidence.id)
                    }
                  }}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  <Clock className="h-4 w-4" />
                  Audit Trail
                  {showAuditTrail ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </button>

                {showAuditTrail && (
                  <div className="mt-2 space-y-2">
                    {loadingAudit ? (
                      <div className="text-center py-4">
                        <RefreshCw className="h-5 w-5 animate-spin mx-auto text-gray-400" />
                      </div>
                    ) : auditLogs.length === 0 ? (
                      <p className="text-sm text-gray-500 py-2">No audit logs</p>
                    ) : (
                      auditLogs.map((log) => (
                        <div key={log.id} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
                          <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                            <Clock className="h-4 w-4 text-gray-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm">
                              <span className="font-medium">{log.user_username || log.username || 'Unknown'}</span>{' '}
                              <span className="text-gray-600">{getActionLabel(log.action)}</span>
                            </p>
                            <p className="text-xs text-gray-500">{formatDate(log.created_at)}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Version Modal (#47) */}
      {showNewVersionModal && selectedEvidence && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Upload New Version</h2>
              <button
                onClick={() => setShowNewVersionModal(false)}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleNewVersion} className="p-4 space-y-4">
              <p className="text-sm text-gray-600">
                Uploading a new version will archive the current file (v{selectedEvidence.current_version || 1}).
              </p>

              <div>
                <label className="label">New File *</label>
                <input
                  ref={newVersionInputRef}
                  type="file"
                  required
                  className="input"
                />
              </div>

              <div>
                <label className="label">Change Reason</label>
                <input
                  type="text"
                  name="change_reason"
                  className="input"
                  placeholder="e.g., Updated with Q4 data"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowNewVersionModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" disabled={uploadingVersion} className="btn-primary">
                  {uploadingVersion ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Version
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Link Controls Modal (#116) */}
      {showLinkControlsModal && selectedEvidence && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Link to Additional Controls</h2>
              <button
                onClick={() => {
                  setShowLinkControlsModal(false)
                  setControlsToLink([])
                }}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4">
              <p className="text-sm text-gray-600 mb-4">
                Select controls to link this evidence to:
              </p>

              <div className="space-y-1 max-h-60 overflow-y-auto border rounded-lg">
                {Object.entries(groupedControls).map(([frameworkName, frameworkControls]) => (
                  <div key={frameworkName}>
                    <div className="px-3 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase sticky top-0">
                      {frameworkName}
                    </div>
                    {frameworkControls.map((c) => {
                      const isLinked = selectedEvidence.control_id === c.id ||
                        selectedEvidence.linked_controls?.some(lc => lc.control_id === c.id)
                      return (
                        <label
                          key={c.id}
                          className={`flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer ${isLinked ? 'opacity-50' : ''}`}
                        >
                          <input
                            type="checkbox"
                            disabled={isLinked}
                            checked={controlsToLink.includes(c.id) || isLinked}
                            onChange={() => {
                              if (!isLinked) {
                                setControlsToLink(prev =>
                                  prev.includes(c.id)
                                    ? prev.filter(id => id !== c.id)
                                    : [...prev, c.id]
                                )
                              }
                            }}
                            className="h-4 w-4 text-primary-600 rounded border-gray-300"
                          />
                          <span className="ml-2 text-sm">
                            {c.control_number} - {c.name}
                          </span>
                          {isLinked && <span className="ml-auto text-xs text-gray-400">Already linked</span>}
                        </label>
                      )
                    })}
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 pt-4 mt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowLinkControlsModal(false)
                    setControlsToLink([])
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLinkControls}
                  disabled={controlsToLink.length === 0 || linkingControls}
                  className="btn-primary"
                >
                  {linkingControls ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Linking...
                    </>
                  ) : (
                    <>
                      <Link2 className="h-4 w-4 mr-2" />
                      Link {controlsToLink.length} Control(s)
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Evidence Preview Modal */}
      {previewEvidence && (
        <EvidencePreview
          isOpen={!!previewEvidence}
          onClose={() => setPreviewEvidence(null)}
          evidenceId={previewEvidence.id}
          fileName={previewEvidence.file_name}
          fileType={previewEvidence.file_type}
          fileSize={previewEvidence.file_size}
          note={previewEvidence.note}
          controlNumber={previewEvidence.control_number}
          uploadedBy={previewEvidence.uploaded_by_username}
          timestamp={previewEvidence.timestamp}
          source={previewEvidence.source}
          metadata={previewEvidence.metadata}
        />
      )}

      {/* Screen Capture Modal (#95) */}
      {showScreenCapture && (
        <ScreenCapture
          onCapture={handleScreenCapture}
          onCancel={() => setShowScreenCapture(false)}
        />
      )}
    </div>
  )
}
